import { mat4, quat, vec3 } from 'gl-matrix';
import { RenderObject } from '../RenderObject';
import { Attachment, AttachmentHolder } from './Attachment';
import { FrameSet } from '../../animation/AnimationState';

// TODO: rename, not Gltf specific
export class GltfAttachmentHolder implements AttachmentHolder {
  private jointAttachments = new Map<string, Attachment[]>();
  private lastJointWorld = new Map<string, mat4>();

  constructor(private parent: RenderObject) {}

  /** Joint rotation+translation world matrix (scale stripped) from the last preRender. Undefined if bone wasn't animated this frame. */
  getBoneWorldMatrix(boneName: string): mat4 | undefined {
    return this.lastJointWorld.get(boneName);
  }

  setAttachment(attachPoint: string, obj: RenderObject): Attachment {
    const attachment = new Attachment(attachPoint, obj, this.parent);
    obj.attachmentIdentity = attachment;
    obj.bounds = this.parent.bounds;
    const list = this.jointAttachments.get(attachPoint) ?? [];
    list.push(attachment);
    this.jointAttachments.set(attachPoint, list);
    return attachment;
  }

  getAttachment(attachPoint: string): Attachment {
    const list = this.jointAttachments.get(attachPoint);
    return list?.[0]!;
  }

  getAttachments(attachPoint: string): Attachment[] {
    return this.jointAttachments.get(attachPoint) ?? [];
  }

  clearAttachment(attachPoint: string): Attachment[] {
    const list = this.jointAttachments.get(attachPoint) ?? [];
    this.jointAttachments.delete(attachPoint);
    for (const a of list) {
      a.object.attachmentIdentity = undefined;
    }
    return list;
  }

  clearSingleAttachment(attachPoint: string, obj: RenderObject): Attachment | undefined {
    const list = this.jointAttachments.get(attachPoint);
    if (!list) return undefined;
    const idx = list.findIndex((a) => a.object === obj);
    if (idx < 0) return undefined;
    const [removed] = list.splice(idx, 1);
    removed.object.attachmentIdentity = undefined;
    if (list.length === 0) this.jointAttachments.delete(attachPoint);
    return removed;
  }

  preRenderAttachments(modelMatrix: mat4): void {
    const animated = this.parent.isAnimated;
    const anim = this.parent.animation;
    let frameSet: FrameSet | undefined;

    if (animated) {
      frameSet = this.parent.animationState!.getFrames();
    }

    this.jointAttachments.forEach((attachments, boneName) => {
      let jointClean: mat4 | undefined;

      if (animated && frameSet) {
        const frame = frameSet.frames[boneName];
        if (frame) {
          let jm = frame.jointMatrix;
          if (anim && this.parent.boneOverrides) {
            const jointIndex = anim.getJointIndex(boneName);
            if (jointIndex !== undefined && this.parent.boneOverrides.has(jointIndex)) {
              jm = this.parent.boneOverrides.get(jointIndex)!;
            }
          }

          // Decompose into position + rotation, discarding scale.
          const pos = mat4.getTranslation(vec3.create(), jm);
          const rot = mat4.getRotation(quat.create(), jm);

          // Sub-frame interpolation from previous frame
          const prevFrame = frameSet.prevFrames?.[boneName];
          if (prevFrame && frameSet.alpha > 0) {
            const prevJm = prevFrame.jointMatrix;
            const prevPos = mat4.getTranslation(vec3.create(), prevJm);
            const prevRot = mat4.getRotation(quat.create(), prevJm);
            vec3.lerp(pos, prevPos, pos, frameSet.alpha);
            quat.slerp(rot, prevRot, rot, frameSet.alpha);
          }

          // State transition blend (e.g., Attack → Idle over 300ms)
          if (frameSet.blend) {
            const blendFrame = frameSet.blend.frames[boneName];
            if (blendFrame) {
              const blendJm = blendFrame.jointMatrix;
              const blendPos = mat4.getTranslation(vec3.create(), blendJm);
              const blendRot = mat4.getRotation(quat.create(), blendJm);
              // weight goes 0→1; at 0 we're fully in the old state, at 1 fully in the new
              vec3.lerp(pos, blendPos, pos, frameSet.blend.weight);
              quat.slerp(rot, blendRot, rot, frameSet.blend.weight);
            }
          }

          jointClean = mat4.fromRotationTranslation(mat4.create(), rot, pos);
        }
      }

      if (jointClean) {
        this.lastJointWorld.set(boneName, jointClean);
      } else {
        this.lastJointWorld.delete(boneName);
      }

      for (const attachment of attachments) {
        const combined = mat4.create();
        if (jointClean) {
          mat4.multiply(combined, jointClean, attachment.offsetMatrix);
        } else {
          mat4.copy(combined, attachment.offsetMatrix);
        }
        const boneMat = mat4.create();
        mat4.transpose(boneMat, combined);

        attachment.modelMatrix = mat4.clone(modelMatrix);
        attachment.boneMatrix = boneMat;
      }
    });
  }
}
